import React, { useState, useEffect } from 'react';

import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TableOfContents from './components/TableOfContents';
import TermsSection from './components/TermsSection';
import ContactCard from './components/ContactCard';
import DisputeResolution from './components/DisputeResolution';
import CustomerRights from './components/CustomerRights';
import Footer from '../../components/Footer';

const TermsOfService = () => {
  const [activeSection, setActiveSection] = useState('acceptance');

  const termsData = [
    {
      id: 'acceptance',
      title: 'Acceptance of Terms',
      icon: 'FileCheck',
      summary: 'By using OpenbooQ, you agree to these terms. Simple as that.',
      content: [
        {
          type: 'paragraph',
          text: 'Welcome to OpenbooQ Marketing Hub. By accessing or using our website, mobile application, or services, you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree with any part of these terms, please do not use our services.'
        },
        {
          type: 'paragraph',
          text: 'These terms apply to all users of OpenbooQ, including homeowners seeking roofing services, contractors in our network, and visitors to our website. We reserve the right to update these terms at any time, with changes taking effect immediately upon posting.'
        },
        {
          type: 'list',
          items: [
            'You must be at least 18 years old to use our services',
            'You must provide accurate and complete information',
            'You are responsible for maintaining the security of your account',
            'You agree to use our services only for lawful purposes'
          ]
        }
      ]
    },
    {
      id: 'services',
      title: 'Our Services',
      icon: 'Home',
      summary: 'We connect homeowners with certified roofing contractors through our transparent platform.',
      content: [
        {
          type: 'paragraph',
          text: 'OpenbooQ operates as a digital marketplace connecting homeowners with pre-vetted, certified roofing contractors. Our platform provides transparent pricing, project management tools, and quality assurance throughout the roofing process.'
        },
        {
          type: 'subsection',
          title: 'Platform Services Include:',
          text: ''
        },
        {
          type: 'list',
          items: [
            'Contractor matching and vetting services',
            'Transparent pricing calculations with no hidden fees',
            'Project management and communication tools',
            'Quality assurance and warranty administration',
            'Customer support throughout the project lifecycle',
            'Dispute resolution and mediation services'
          ]
        },
        {
          type: 'paragraph',
          text: 'We charge one transparent 20% Service & Warranty fee on completed projects, with no markups on materials or labor costs. This fee covers our service coordination, quality assurance, warranty backing, and customer support.'
        }
      ]
    },
    {
      id: 'pricing',
      title: 'Pricing & Payment',
      icon: 'CreditCard',
      summary: 'Transparent pricing with one simple 20% Service & Warranty fee. No deposit — you pay after your roof passes final inspection.',
      content: [
        {
          type: 'paragraph',
          text: 'Our pricing model is built on radical transparency. Unlike traditional contractors who mark up materials and hide costs, we show you exactly what everything costs and charge only a 20% Service & Warranty fee for our services.'
        },
        {
          type: 'subsection',
          title: 'How Pricing Works:',
          text: ''
        },
        {
          type: 'list',
          items: [
            'Material costs are shown at actual wholesale prices',
            'Labor costs are transparent and competitive',
            'Our 20% Service & Warranty fee is clearly displayed',
            'No hidden fees, markups, or surprise charges',
            'Payment is processed securely through our platform',
            'Escrow is available if you want it — funds are released only once you are satisfied'
          ]
        },
        {
          type: 'paragraph',
          text: 'Payment terms are set out in your project agreement. There is no deposit: $0 is due at signing and $0 is due while the work is happening. Payment is due only after your new roof is installed and you have walked and approved the finished work at final inspection. Monthly financing is available through Enhancify, and you may also pay upfront or through escrow. All payments are processed through secure, encrypted channels.'
        }
      ]
    },
    {
      id: 'warranty',
      title: 'Warranty & Guarantees',
      icon: 'Shield',
      summary: 'Comprehensive 3-year warranty on workmanship, plus manufacturer warranties on materials.',
      content: [
        {
          type: 'paragraph',
          text: 'We stand behind every project with our comprehensive 3-year workmanship warranty, in addition to manufacturer warranties on all materials. This dual protection ensures your investment is protected.'
        },
        {
          type: 'subsection',
          title: 'Warranty Coverage Includes:',
          text: ''
        },
        {
          type: 'list',
          items: [
            'Workmanship defects for 3 years from completion',
            'Material defects per manufacturer warranty terms',
            'Leak repairs at no cost during warranty period',
            'Annual inspection services included',
            'Emergency repair response within 24 hours',
            'Full warranty transferability to new homeowners'
          ]
        },
        {
          type: 'paragraph',
          text: 'To maintain warranty coverage, routine maintenance as outlined in your warranty documentation is required. We provide clear maintenance guidelines and reminder services to help protect your investment.'
        }
      ]
    },
    {
      id: 'contractors',
      title: 'Contractor Network',
      icon: 'Users',
      summary: 'All contractors are vetted, licensed, insured, and continuously monitored for quality.',
      content: [
        {
          type: 'paragraph',
          text: 'Every contractor in our network undergoes rigorous vetting before approval and continuous monitoring throughout their partnership with OpenbooQ. We maintain high standards to ensure quality and reliability.'
        },
        {
          type: 'subsection',
          title: 'Contractor Requirements:',
          text: ''
        },
        {
          type: 'list',
          items: [
            'Valid state licensing and local permits',
            'Comprehensive liability and workers compensation insurance',
            'Minimum 5 years of roofing experience',
            'Background checks and reference verification',
            'Ongoing performance monitoring and customer feedback',
            'Commitment to our quality standards and processes'
          ]
        },
        {
          type: 'paragraph',
          text: 'While we carefully vet our contractors, the contractual relationship for your roofing work is between you and the contractor. OpenbooQ facilitates the connection and provides oversight, but the contractor is responsible for the actual work performed.'
        }
      ]
    },
    {
      id: 'liability',
      title: 'Liability & Insurance',
      icon: 'Umbrella',
      summary: 'Clear liability structure with comprehensive insurance coverage protecting all parties.',
      content: [
        {
          type: 'paragraph',
          text: 'OpenbooQ maintains comprehensive insurance coverage and requires the same from all network contractors. This multi-layered protection ensures all parties are covered in the unlikely event of issues.'
        },
        {
          type: 'subsection',
          title: 'Insurance Coverage:',
          text: ''
        },
        {
          type: 'list',
          items: [
            'General liability insurance up to $2 million per occurrence',
            'Workers compensation coverage for all workers',
            'Professional liability insurance for platform services',
            'Cyber liability protection for data security',
            'Errors and omissions coverage for service delivery',
            'Additional coverage through contractor insurance requirements'
          ]
        },
        {
          type: 'paragraph',
          text: 'OpenbooQ\'s liability is limited to the service fees paid for platform services. Contractors maintain primary liability for workmanship and materials. Our role is to facilitate connections and provide oversight, not to perform roofing work directly.'
        }
      ]
    },
    {
      id: 'privacy',
      title: 'Privacy & Data Protection',
      icon: 'Lock',
      summary: 'Your personal information is protected with enterprise-grade security and clear usage policies.',
      content: [
        {
          type: 'paragraph',
          text: 'We take your privacy seriously and implement enterprise-grade security measures to protect your personal information. Our data practices are transparent and designed to give you control over your information.'
        },
        {
          type: 'subsection',
          title: 'Data Protection Measures:',
          text: ''
        },
        {
          type: 'list',
          items: [
            'End-to-end encryption for all sensitive data',
            'Secure data centers with 24/7 monitoring',
            'Regular security audits and penetration testing',
            'Strict access controls and employee training',
            'GDPR and CCPA compliance where applicable',
            'Clear data retention and deletion policies'
          ]
        },
        {
          type: 'paragraph',
          text: 'For complete details on how we collect, use, and protect your information, please review our Privacy Policy. We never sell your personal information to third parties and only share data as necessary to provide our services.'
        }
      ]
    },
    {
      id: 'termination',
      title: 'Account Termination',
      icon: 'UserX',
      summary: 'Either party can terminate the relationship with proper notice and fair resolution of ongoing projects.',
      content: [
        {
          type: 'paragraph',
          text: 'You may terminate your OpenbooQ account at any time by contacting our customer service team. We may terminate accounts for violations of these terms or other legitimate business reasons.'
        },
        {
          type: 'subsection',
          title: 'Termination Process:',
          text: ''
        },
        {
          type: 'list',
          items: [
            'Account closure requests processed within 48 hours',
            'Ongoing projects completed according to existing agreements',
            'Refunds processed for unused services within 30 days',
            'Data deletion according to our retention policies',
            'Warranty obligations remain in effect post-termination',
            'Final account statements provided within 7 business days'
          ]
        },
        {
          type: 'paragraph',
          text: 'Termination does not affect completed transactions, warranty obligations, or dispute resolution proceedings already in progress. All parties remain bound by confidentiality and other surviving provisions.'
        }
      ]
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const sections = termsData?.map(section => section?.id);
      const scrollPosition = window.scrollY + 200;

      for (let i = sections?.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections?.[i]);
        if (element && element?.offsetTop <= scrollPosition) {
          setActiveSection(sections?.[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element?.offsetTop;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Terms of Service | OpenbooQ</title>
        <meta name="description" content="Review the OpenbooQ Terms of Service. Understand our user agreements, policies, and guidelines for using our transparent roofing platform." />
      </Helmet>
      <div className="min-h-screen bg-surface">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-24 pb-12 bg-gradient-to-br from-primary/5 to-secondary/5">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="FileText" size={24} className="text-white" />
                </div>
                <div>
                  <h1 className="font-jakarta font-bold text-4xl text-text-primary">
                    Terms of Service
                  </h1>
                  <p className="text-text-secondary font-inter">
                    Transparent policies that build trust, not barriers
                  </p>
                </div>
              </div>
              
              <p className="text-lg font-inter text-text-secondary mb-6">
                We believe legal terms should be clear and fair. These terms outline our 
                commitment to transparency, quality, and customer protection in plain English.
              </p>
              
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-lg border border-border">
                  <Icon name="Calendar" size={16} className="text-primary" />
                  <span className="text-sm font-inter text-text-primary">
                    Last Updated: November 2, 2025
                  </span>
                </div>
                <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-lg border border-border">
                  <Icon name="GitBranch" size={16} className="text-primary" />
                  <span className="text-sm font-inter text-text-primary">
                    Version 2.1
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-4 gap-8">
              {/* Table of Contents */}
              <div className="lg:col-span-1">
                <TableOfContents 
                  sections={termsData}
                  activeSection={activeSection}
                  onSectionClick={scrollToSection}
                />
              </div>

              {/* Terms Content */}
              <div className="lg:col-span-3">
                <div className="space-y-8">
                  {/* Introduction */}
                  <div className="bg-white rounded-xl border border-border p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name="Heart" size={24} className="text-primary" />
                      </div>
                      <div>
                        <h2 className="font-jakarta font-bold text-2xl text-text-primary mb-3">
                          Our Commitment to You
                        </h2>
                        <p className="text-text-secondary font-inter mb-4">
                          These terms reflect our core values: transparency, fairness, and customer empowerment. 
                          We've written them in plain English because we believe you should understand exactly what you're agreeing to.
                        </p>
                        <div className="grid sm:grid-cols-3 gap-4">
                          <div className="text-center p-3 bg-surface rounded-lg">
                            <Icon name="Eye" size={20} className="text-primary mx-auto mb-2" />
                            <p className="text-sm font-inter font-medium text-text-primary">
                              Transparent
                            </p>
                          </div>
                          <div className="text-center p-3 bg-surface rounded-lg">
                            <Icon name="Scale" size={20} className="text-primary mx-auto mb-2" />
                            <p className="text-sm font-inter font-medium text-text-primary">
                              Fair
                            </p>
                          </div>
                          <div className="text-center p-3 bg-surface rounded-lg">
                            <Icon name="Shield" size={20} className="text-primary mx-auto mb-2" />
                            <p className="text-sm font-inter font-medium text-text-primary">
                              Protected
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Terms Sections */}
                  {termsData?.map((section) => (
                    <TermsSection 
                      key={section?.id}
                      section={section}
                      isActive={activeSection === section?.id}
                    />
                  ))}

                  {/* Customer Rights */}
                  <CustomerRights />

                  {/* Dispute Resolution */}
                  <DisputeResolution />

                  {/* Contact Information */}
                  <ContactCard />

                  {/* Footer Navigation */}
                  <div className="bg-white rounded-xl border border-border p-6">
                    <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                      <div>
                        <h2 className="font-jakarta font-semibold text-lg text-text-primary mb-1">
                          Ready to Get Started?
                        </h2>
                        <p className="text-text-secondary font-inter text-sm">
                          Join thousands of homeowners who trust OpenbooQ for their roofing needs
                        </p>
                      </div>
                      <div className="flex space-x-3">
                        <Button 
                          variant="outline"
                          iconName="FileText"
                          iconPosition="left"
                          onClick={() => window.location.href = '/privacy-policy'}
                        >
                          Privacy Policy
                        </Button>
                        <Button 
                          variant="default"
                          iconName="ArrowRight"
                          iconPosition="right"
                          onClick={() => window.location.href = '/instant-estimate'}
                        >
                          Get Started
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SEO Body Copy Section */}
        <section className="py-12 bg-gray-50 border-t border-gray-200">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Understanding Your Agreement with OpenbooQ</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              OpenbooQ operates as a transparent digital marketplace connecting homeowners nationwide with certified roofing contractors. Our Terms of Service are designed to be clear, fair, and easy to understand — because we believe the same transparency we apply to pricing should apply to our legal agreements as well.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              When you use OpenbooQ to get a roof replacement price, you're engaging with a platform that charges a single, transparent 20% Service & Warranty fee on completed projects. This fee covers our contractor vetting process, quality assurance inspections, warranty administration, and dedicated customer support throughout your roofing project. There are no hidden fees, no material markups, and no surprise charges.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Our platform connects homeowners with pre-screened, licensed, and insured roofing contractors who have agreed to OpenbooQ's quality and pricing standards. By using our services, both homeowners and contractors agree to maintain the transparency and professionalism that defines the OpenbooQ experience. If you have questions about any aspect of these terms, our support team is available to help clarify any provisions.
            </p>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default TermsOfService;